// useLookup — the read behind every picker of a big entity (ADR-078 §6): ONE
// bounded read per entity — the 20 best by the typed search and `has_more`, or
// the rows of given ids — under the surface's named RULE (the server applies
// it). What the hook owns, for every picker alike (FRONTEND-PATTERNS §Option
// sources): the wait while typing (the debounce) · the previous answer kept on
// screen while the next is in flight — across the typed text, never across a
// change of source (another site's units are not this site's) · EVERY ROW MET
// kept — ONCE PER SOURCE, a cache entry every instance on that source reads and
// writes, so a row the field met by typing names the chosen in its host's hook
// too — so what is chosen stays named after the search moved on · the names of
// the ids a link, a saved view or an edit form carries (asked in batches of the
// answer's cap) · a late answer never over a newer one (the query key IS the
// typed text) · a failed read said so (`failed`), never shown as an empty
// answer. The family's `useOptions` (the server
// kind) and a screen that needs the START answer (the via pre-fill; the wizard's
// generic preselect through `lookupSearchOptions`) ride it and share the cache
// entry. A CLOSED picker (`open: false`) asks nothing for its
// list — only the NAMES of what is already chosen (a value in the link at page
// open — ADR-080 §7), at once. The PRODUCTS ride the same hook over the
// catalogue's search (`asset-models/search`): one category, the generic row
// pinned last by the server, "Used in this project" first — a read that NAMES
// no ids (no screen brings a bare product id; an edit form seeds the row it
// holds as `known` — naming only, never listing).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { queryOptions, skipToken, useQueries, useQuery, useQueryClient, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { LOOKUP_CAP } from '@ems-app/schemas/common/lookup';
import type { SiteLookupResponse, SiteLookupRow } from '@ems-app/schemas/sites/lookup';
import type { AssetLookupResponse, AssetLookupRow } from '@ems-app/schemas/assets/lookup';
import type { UserLookupResponse, UserLookupRow } from '@ems-app/schemas/users/lookup';
import type { VehicleLookupResponse, VehicleLookupRow } from '@ems-app/schemas/vehicles/lookup';
import type { AssetModelSearchItem, AssetModelSearchResponse } from '@ems-app/schemas/catalog/responses';
import { apiClient } from '../lib/apiClient.js';
import { useDebouncedValue } from './useDebouncedValue.js';

export type LookupEntity = 'sites' | 'assets' | 'users' | 'vehicles' | 'asset-models';
export type LookupRowOf<E extends LookupEntity> = E extends 'sites' ? SiteLookupRow : E extends 'assets' ? AssetLookupRow : E extends 'users' ? UserLookupRow : E extends 'vehicles' ? VehicleLookupRow : AssetModelSearchItem;
type AnswerOf<E extends LookupEntity> = E extends 'sites' ? SiteLookupResponse : E extends 'assets' ? AssetLookupResponse : E extends 'users' ? UserLookupResponse : E extends 'vehicles' ? VehicleLookupResponse : AssetModelSearchResponse;

/** Each entity's bounded read under the project's prefix, and whether it NAMES given ids (`?ids=`): the lookups
 *  do; the products' search does not — a form that holds a product seeds its row (`known`). */
const READS: Record<LookupEntity, { path: string; names: boolean }> = {
  sites: { path: 'sites/lookup', names: true },
  assets: { path: 'assets/lookup', names: true },
  users: { path: 'users/lookup', names: true },
  vehicles: { path: 'vehicles/lookup', names: true },
  'asset-models': { path: 'asset-models/search', names: false },
};

/** The question a picker asks: the entity, its rule and the rule's container
 *  (`site` · `level` + `scope` · `self` · `placing` · `category`). */
export interface LookupSource<E extends LookupEntity = LookupEntity> {
  entity: E;
  params: Readonly<Record<string, string | undefined>>;
}

export const lookupKeys = {
  search: (projectId: string, source: LookupSource, q: string) => ['lookup', projectId, source.entity, source.params, q] as const,
  named: (projectId: string, source: LookupSource, ids: readonly string[]) => ['lookup-ids', projectId, source.entity, source.params, ids] as const,
  /** The rows MET on a source — every answer's rows, kept once per source for every hook instance on it. */
  met: (projectId: string, source: LookupSource) => ['lookup-met', projectId, source.entity, source.params] as const,
};

/** Forget what the pickers met on an entity, every source: the shared rows reset, the named and searched answers asked
 *  again — after a write that changes a FACT the rows carry (a consent that backs a person: the `accountable` rule's
 *  covered / member flags). A hook instance keeps its batches, so the names come back from the fresh answers. */
export function forgetLookupRows(qc: QueryClient, projectId: string, entity: LookupEntity): void {
  void qc.resetQueries({ queryKey: ['lookup-met', projectId, entity] });
  void qc.invalidateQueries({ queryKey: ['lookup-ids', projectId, entity] });
  void qc.invalidateQueries({ queryKey: ['lookup', projectId, entity] });
}

async function fetchLookup<E extends LookupEntity>(projectId: string, source: LookupSource<E>, extra: Record<string, string>): Promise<AnswerOf<E>> {
  const params: Record<string, string> = { ...extra };
  for (const [k, v] of Object.entries(source.params)) if (v !== undefined && v !== '') params[k] = v;
  const r = await apiClient.get<AnswerOf<E>>(`/projects/${projectId}/${READS[source.entity].path}`, { params });
  return r.data;
}

const DEBOUNCE_MS = 250;
const STALE_MS = 30_000;

/** The START (`q` empty) or SEARCH read of a source as query options — ONE place for its key, its fetch and its
 *  freshness, so a host that asks the answer outside the hook (`queryClient.fetchQuery` — the wizard's generic
 *  preselect) shares the field's own cache entry and never asks twice. */
export function lookupSearchOptions<E extends LookupEntity>(projectId: string, source: LookupSource<E>, q: string) {
  return queryOptions({ queryKey: lookupKeys.search(projectId, source, q), queryFn: () => fetchLookup(projectId, source, q ? { q } : {}), staleTime: STALE_MS });
}

export interface Lookup<E extends LookupEntity> {
  /** The live input text — the field writes it, the hook searches it. */
  input: string;
  setInput: (text: string) => void;
  /** Something is typed. */
  typing: boolean;
  /** Typed, and the answer for that text is not settled yet (the debounce pending, or the fetch in flight). */
  searching: boolean;
  /** The answer's rows for the settled text (the previous answer while the next is in flight). */
  rows: readonly LookupRowOf<E>[];
  hasMore: boolean;
  /** Every row met on this source — the answers and the named ids, by every instance on it — by id: what names a chosen value. */
  known: ReadonlyMap<string, LookupRowOf<E>>;
  /** The chosen ids' rows, in the chosen order, as far as they are known. */
  chosenRows: readonly LookupRowOf<E>[];
  /** Some chosen ids are still being named. */
  naming: boolean;
  /** A chosen id the server answered ABSENT to (not covered, or gone): its chip says "not available". */
  unavailable: ReadonlySet<string>;
  /** The START answer (nothing typed) has arrived — or failed — a screen that seeds a form from it waits for this. */
  settled: boolean;
  /** The search, or the naming of the chosen ids, failed (after the client's own retry): the field says so, never an empty answer. */
  failed: boolean;
}

export interface LookupOptions<E extends LookupEntity = LookupEntity> {
  /** False = nothing is asked at all (a host that pre-mounts a closed drawer). Default true. */
  enabled?: boolean;
  /** False = the picker is closed: the start list and the search are not asked; the chosen ids are still NAMED. Default true. */
  open?: boolean;
  /** The typed text, when the host owns it (the family's picker) — else the hook keeps its own (`setInput`). */
  text?: string;
  /** Rows the host already holds (an edit form's product) — this instance's own, so what is chosen is named before
   *  any read; the answer's own row replaces a seeded one when it arrives (a seed never reaches the shared rows). */
  known?: readonly LookupRowOf<E>[];
  /** The source whose SHARED rows this instance reads and writes — default its own. A list that is a window over
   *  another source's population (one kind of a site's units beside the site's whole) remembers its rows there, so
   *  the host's naming on the wider source finds a row any window met. */
  memory?: LookupSource<E>;
}

export function useLookup<E extends LookupEntity>(projectId: string, source: LookupSource<E>, chosen: readonly string[], options: boolean | LookupOptions<E> = {}): Lookup<E> {
  const { enabled = true, open = true, text: controlled, known: seeded, memory = source } = typeof options === 'boolean' ? { enabled: options } : options;
  const [input, setInput] = useState('');
  const text = (controlled ?? input).trim();
  const q = useDebouncedValue(text, DEBOUNCE_MS);
  const sourceKey = JSON.stringify([projectId, source.entity, source.params]);
  const live = enabled && open && projectId.length > 0;
  // Typed-first: a text typed before the start list was asked skips the start list's FETCH — the typed answer is the
  // first one asked (a field opened by a letter, the files' search box), never a start list fetched only to be
  // replaced 250 ms later. Through that window the hook is `searching` (never "no results"), and a start answer the
  // cache already holds stays on screen as the rows — the previous answer kept, as during any search in flight.
  const typedFirst = text.length > 0 && q === '';
  const asking = live && !typedFirst;
  const search = useQuery({
    ...lookupSearchOptions(projectId, source, q),
    enabled: asking,
    // The previous answer stays on screen while the next text's is in flight — for the SAME source only.
    placeholderData: (prev, prevQuery) => (prevQuery && JSON.stringify(prevQuery.queryKey.slice(1, 4)) === sourceKey ? prev : undefined),
  });
  // A closed or disabled picker's cached data is not its answer — only an open hook has rows.
  const fetched = live ? (search.data?.items as readonly LookupRowOf<E>[] | undefined) : undefined;

  // Every row met is kept ONCE PER SOURCE — a cache entry (no read of its own) that every instance on the source
  // reads and, after each answer, adds its rows to: a chosen row that left the list stays named, and a row the FIELD
  // met by typing names the chosen in its HOST's hook too (the asset form's product — the one entity whose read names
  // no ids). The rows a host seeded (`known`) are this instance's own: they name the chosen before any read and give
  // way to the answer's row (merged under it), and never reach the shared rows (a seed is thin).
  const queryClient = useQueryClient();
  const seededMap = useMemo(() => new Map<string, LookupRowOf<E>>((seeded ?? []).map((row) => [row.id, row])), [seeded]);
  const memoryKey = JSON.stringify([projectId, memory.entity, memory.params]);
  const met = useQuery<ReadonlyMap<string, LookupRowOf<E>>>({ queryKey: lookupKeys.met(projectId, memory), queryFn: skipToken, staleTime: Infinity }).data;

  // The names of the chosen ids the field has not met — asked AFTER the first
  // answer (the start often names them already), in batches of the cap; at once
  // when the picker is closed (nothing else will name them). A batch once asked
  // keeps its ids: a batch that shrank as rows arrived would be a second request
  // for the same names (two racing on a link open, seen live).
  const [batches, setBatches] = useState<readonly (readonly string[])[]>([]);
  if (READS[source.entity].names && (!asking || fetched !== undefined || search.isError)) {
    const covered = new Set(batches.flat());
    const missing = chosen.filter((id) => !covered.has(id) && !seededMap.has(id) && !met?.has(id) && !fetched?.some((r) => r.id === id));
    if (missing.length > 0) {
      const next = [...batches];
      for (let i = 0; i < missing.length; i += LOOKUP_CAP) next.push(missing.slice(i, i + LOOKUP_CAP));
      setBatches(next);
    }
  }
  // `combine` (a stable function) hands back ONE structurally shared result, so
  // `namedRows` keeps its identity between renders — the render-time guard
  // below needs that (a fresh array each render would loop the render).
  const combine = useCallback(
    (results: UseQueryResult<AnswerOf<E>, Error>[]) => ({
      rows: results.flatMap((r) => (r.data?.items ?? []) as LookupRowOf<E>[]),
      fetching: results.some((r) => r.isFetching),
      failed: results.some((r) => r.isError),
      answered: results.map((r) => (r.isSuccess ? r.data.items.map((row) => row.id) : null)),
    }),
    [],
  );
  const named = useQueries({
    queries: batches.map((ids) => ({
      queryKey: lookupKeys.named(projectId, source, ids),
      queryFn: () => fetchLookup(projectId, source, { ids: ids.join(',') }),
      enabled: enabled && projectId.length > 0,
      staleTime: STALE_MS,
    })),
    combine,
  });
  const namedRows = named.rows;
  // The answers' rows join the source's shared rows — a cache write after the render that showed them (the one place
  // the family writes a cache entry by hand); nothing is written when every row is already there.
  useEffect(() => {
    const rows = [...(fetched ?? []), ...namedRows];
    if (rows.length === 0) return;
    const key = lookupKeys.met(projectId, memory);
    const prev = queryClient.getQueryData<ReadonlyMap<string, LookupRowOf<E>>>(key);
    if (prev && rows.every((row) => prev.get(row.id) === row)) return;
    const next = new Map(prev);
    for (const row of rows) next.set(row.id, row);
    queryClient.setQueryData(key, next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the memory source stands in by its key string
  }, [fetched, namedRows, projectId, memoryKey, queryClient]);
  const naming = named.fetching;
  // Only an ANSWER says a code is absent — a failed batch says nothing.
  const unavailable = useMemo(() => {
    const out = new Set<string>();
    named.answered.forEach((got, i) => {
      if (got) for (const id of batches[i] ?? []) if (!got.includes(id)) out.add(id);
    });
    return out;
  }, [named.answered, batches]);

  const known = useMemo(() => {
    const all = new Map(seededMap);
    if (met) for (const [id, row] of met) all.set(id, row);
    for (const row of fetched ?? []) all.set(row.id, row);
    for (const row of namedRows) all.set(row.id, row);
    return all;
  }, [seededMap, met, fetched, namedRows]);

  const typing = text.length > 0;
  // Typed and not answered for that text yet: the debounce pending (the typed-first window included), or the fetch in flight.
  const searching = live && typing && (q !== text || search.isPlaceholderData || search.isFetching);
  const rows = useMemo(() => fetched ?? [], [fetched]);
  const chosenRows = useMemo(() => chosen.map((id) => known.get(id)).filter((r): r is LookupRowOf<E> => r !== undefined), [chosen, known]);
  const settled = !live || search.isError || (q === '' && search.data !== undefined && !search.isPlaceholderData) || (q !== '' && fetched !== undefined);
  return { input: controlled ?? input, setInput, typing, searching, rows, hasMore: search.data?.has_more ?? false, known, chosenRows, naming, unavailable, settled, failed: search.isError || named.failed };
}
